import moment from "moment";
import { knownTaxKeys } from "../_constants/constant";
import {
  calculateFareDetails,
  calculateFareDetailsFromReprint,
} from "./fareUtils";
import { getLocalItem } from "./storageUtils";

export const formatTraveller = (
  traveller: any,
  type: string,
  requiredetails?: Record<string, boolean | null | number>
) => {
  const baseDetails: Record<string, any> = {
    Pax_type: type,
    Title: traveller.initial || "",
    First_Name: traveller.first_name,
    Last_Name: traveller.last_name || "",
    Gender: traveller.initial
      ? traveller.initial === "MR" || traveller.initial === "MSTR"
        ? "0"
        : "1"
      : "0",
    Age: traveller.date_of_birth
      ? `${moment().diff(traveller.date_of_birth, "years")}`
      : null,
    DOB: traveller.date_of_birth
      ? moment(traveller.date_of_birth).format("MM/DD/YYYY")
      : "",
    Passport_Number: traveller.passport_no || "",
    Passport_Issuing_Country: traveller.issue_country_code || "",
    Passport_Expiry: traveller.expire_date
      ? moment(traveller.expire_date, "YYYY-MM-DD").format("MM/DD/YYYY")
      : "",
    Student_Id: traveller.student_id || "",
    DefenceServiceId: traveller.defence_service_id || "",
    DefenceIssueDate: traveller.defence_issue_date
      ? moment(traveller.defence_issue_date, "YYYY-MM-DD").format("MM/DD/YYYY")
      : "",
    DefenceExpiryDate: traveller.defence_expiry_date
      ? moment(traveller.defence_expiry_date, "YYYY-MM-DD").format("MM/DD/YYYY")
      : "",
    Nationality: traveller.nationality || "",
    Pancard_Number: traveller.pancard_number || "",
    FrequentFlyerDetails: traveller?.FrequentFlyer_details || null,
  };
  if (!requiredetails || Object.keys(requiredetails).length === 0) {
    return baseDetails;
  }
  const filteredDetails = Object.keys(requiredetails).reduce((acc, key) => {
    let value: any;
    const actualKey = key === "PanCard_No" ? "Pancard_Number" : key;
    if (requiredetails[key] === true) {
      value = baseDetails[actualKey];
    } else {
      const baseValue = baseDetails[actualKey];
      if (Array.isArray(baseValue)) {
        value = [];
      } else if (typeof baseValue === "string") {
        value = null;
      } else if (typeof baseValue === "number") {
        value = null;
      } else {
        value = null;
      }
    }
    acc[key] = value;
    return acc;
  }, {} as Record<string, any>);
  return {
    ...filteredDetails,
    Pax_type: type,
    Gender: traveller.initial
      ? traveller.initial === "MR" || traveller.initial === "MSTR"
        ? "0"
        : "1"
      : "0",
  };
};

export const formatBookingData = (reprintResponse: any) => {
  const user_guid = getLocalItem("user_guid");

  const faredetails = calculateFareDetailsFromReprint(
    reprintResponse?.AirPNRDetails || []
  );

  const IsRefundable =
    reprintResponse?.AirPNRDetails &&
    reprintResponse?.AirPNRDetails[0] &&
    reprintResponse?.AirPNRDetails[0]?.Flights &&
    reprintResponse?.AirPNRDetails[0]?.Flights[0]?.Fares &&
    reprintResponse?.AirPNRDetails[0]?.Flights[0]?.Fares[0]?.Refundable;

  const RefundableType = IsRefundable ? "Refundable" : "Non Refundable";

  const booking_status =
    reprintResponse?.AirPNRDetails &&
    reprintResponse?.AirPNRDetails[0] &&
    reprintResponse?.AirPNRDetails[0]?.Ticket_Status_Desc;

  // Count only Adults + Children (exclude infants)
  const eligiblePaxCount =
    reprintResponse?.AirPNRDetails?.reduce((count: number, pnr: any) => {
      return (
        count +
        (pnr?.PAXTicketDetails?.filter(
          (traveller: any) =>
            traveller?.Pax_type === 0 || traveller?.Pax_type === 1 // 0=Adult, 1=Child
        ).length || 0)
      );
    }, 0) || 1;

  // Total convenience fee including GST
  const totalConvenienceFee = reprintResponse?.convenienceFeeWithGST || 0;
  const gstPer = reprintResponse?.convenienceFeeGSTPER || 0;

  // Base fee (excluding GST)
  const baseConvenienceFee = totalConvenienceFee / (1 + gstPer / 100);

  // Per eligible pax share
  const perEligiblePaxFee =
    eligiblePaxCount > 0 ? baseConvenienceFee / eligiblePaxCount : 0;

  const bookingData: any = {
    user_guid: user_guid,
    booking_no: reprintResponse?.Booking_RefNo,
    portal_booking_no: reprintResponse?.portal_booking_no,
    convenience_fee: reprintResponse?.convenienceFeeWithGST,
    convenience_fee_gst_per: reprintResponse?.convenienceFeeGSTPER,
    PromoCodeID: reprintResponse?.appliedPromo?.ID,
    PromoCode: reprintResponse?.appliedPromo?.PromoCode,
    PromoCodeValue: reprintResponse?.appliedPromoAmount,
    GiftCode: reprintResponse?.appliedGiftCode,
    GiftCodeValue: reprintResponse?.appliedGiftCodeAmount,
    wallet_balance: reprintResponse?.walletAmount,
    refundable_walletamount: reprintResponse?.walletRefundableAmount,
    non_refundable_walletamount: reprintResponse?.walletNonRefundableAmount,
    IsCreditLimitUsed: reprintResponse?.creditLimitAmount > 0,
    CreditLimitUsedAmount: reprintResponse?.creditLimitAmount,
    travel_type: reprintResponse?.Travel_Type,
    booking_status: booking_status, // TODO : need to pass dynamic based on actual status
    booking_date: moment(
      reprintResponse?.Booking_DateTime,
      "DD/MM/YYYY HH:mm:ss"
    ).format("YYYY-MM-DD[T]HH:mm:ss"),
    amount:
      (faredetails?.adults?.total || 0) +
      (faredetails?.children?.total || 0) +
      (faredetails?.infants?.total || 0),
    // (reprintResponse?.convenienceFeeWithGST || 0),
    // (faredetails?.overall?.serviceAmount || 0) +
    // (reprintResponse?.totalSeatsAmount || 0) +
    // (reprintResponse?.totalBaggageAmount || 0) +
    // (reprintResponse?.totalWheelChairAmount || 0) +
    // (reprintResponse?.totalMealAmount || 0),
    email_id: reprintResponse?.PAX_EmailId,
    mobile_no: reprintResponse?.PAX_Mobile,
    // alt_email_id: reprintResponse?.deliveryInfo?.emails[1] || "",
    // alt_mobile_no: reprintResponse?.deliveryInfo?.contacts[1] || "",
    gst_number: reprintResponse?.CustomerDetail?.GST_Number,
    // gst_email: reprintResponse?.CustomerDetail?.GST_Number,
    gst_registred_name: reprintResponse?.CustomerDetail?.GSTHolderName,
    // gst_mobile_no: reprintResponse?.CustomerDetail?.GST_Number,
    gst_address: reprintResponse?.CustomerDetail?.GSTAddress,
    state_id: reprintResponse?.gst_state_id,
    base_fare:
      (faredetails?.adults?.basic || 0) +
      (faredetails?.children?.basic || 0) +
      (faredetails?.infants?.basic || 0),
    airline_gst:
      (faredetails?.adults?.gst || 0) +
      (faredetails?.children?.gst || 0) +
      (faredetails?.infants?.gst || 0),
    total_airline_tax:
      (faredetails?.adults?.taxes || 0) +
      (faredetails?.children?.taxes || 0) +
      (faredetails?.infants?.taxes || 0),
    service_fee: faredetails?.overall?.serviceAmount || 0,
    // (faredetails?.overall?.serviceAmount || 0)
    // // other_taxes: itemInfos?.AIR?.totalPriceInfo?.totalFareDetail?.afC?.TAF?.OT,
    // // yr: itemInfos?.AIR?.totalPriceInfo?.totalFareDetail?.afC?.TAF?.YR,
    // // oc: itemInfos?.AIR?.totalPriceInfo?.totalFareDetail?.afC?.TAF?.OC,
    net_price:
      (faredetails?.adults?.total || 0) +
      (faredetails?.children?.total || 0) +
      (faredetails?.infants?.total || 0),
    // (reprintResponse?.convenienceFeeWithGST || 0),
    // (faredetails?.overall?.serviceAmount || 0) +
    // (reprintResponse?.totalSeatsAmount || 0) +
    // (reprintResponse?.totalBaggageAmount || 0) +
    // (reprintResponse?.totalWheelChairAmount || 0) +
    // (reprintResponse?.totalMealAmount || 0)
    fare_rules: RefundableType,
    flight_details: [],
    passenger_details: [],
  };

  reprintResponse?.AirPNRDetails?.forEach(
    (pnrDetail: any, outerIndex: number) => {
      pnrDetail?.Flights?.forEach((flight: any) => {
        const fareClass =
          flight?.Fares &&
          flight?.Fares[0]?.FareDetails &&
          flight?.Fares[0]?.FareDetails[0] &&
          flight?.Fares[0]?.FareDetails[0]?.FareClasses &&
          flight?.Fares[0]?.FareDetails[0]?.FareClasses[0] &&
          flight?.Fares[0]?.FareDetails[0]?.FareClasses[0];

        flight?.Segments?.forEach((segment: any) => {
          bookingData?.flight_details?.push({
            airline_name: segment?.Airline_Name,
            airline_code: segment?.Airline_Code,
            flight_no: segment?.Flight_Number,
            flight_class: fareClass?.Class_Desc,
            flight_class_code: fareClass?.Class_Code,
            flight_type: reprintResponse?.tripType || "ONEWAY",
            departure_date_time: moment(
              segment?.Departure_DateTime,
              "MM/DD/YYYY HH:mm:ss"
            ).format("YYYY-MM-DD[T]HH:mm:ss"),
            departure_place: segment?.Origin,
            departure_airport: segment?.Origin,
            departure_terminal: segment?.Origin_Terminal,
            arrival_date_time: moment(
              segment?.Arrival_DateTime,
              "MM/DD/YYYY HH:mm:ss"
            ).format("YYYY-MM-DD[T]HH:mm:ss"),
            arrival_place: segment?.Destination,
            arrival_airport: segment?.Destination,
            arrival_terminal: segment?.Destination_Terminal,
            duration: segment?.Duration,
            baggage_information: "",
            stop_details:
              !segment?.Stop_Over || segment?.Stop_Over === 0
                ? "Non-stop"
                : segment?.Stop_Over + " Stop",
            pnr: pnrDetail?.Airline_PNR || "",
            crs_pnr: pnrDetail?.CRS_PNR || "",
            journey_type: segment?.Return_Flight ? "Return" : "Onward",
            class_description: fareClass?.Class_Desc,
            aircraft_type: segment?.Aircraft_Type,
            is_refundable: IsRefundable,
            booking_status: booking_status,
          });
        });
      });
    }
  );

  reprintResponse?.AirPNRDetails?.forEach(
    (pnrDetail: any, outerIndex: number) => {
      pnrDetail?.PAXTicketDetails?.forEach((traveller: any) => {
        const fareDetails =
          traveller?.Fares &&
          traveller?.Fares[0] &&
          traveller?.Fares[0]?.FareDetails &&
          traveller?.Fares[0]?.FareDetails[0];

        const baggage_details = fareDetails?.Free_Baggage;

        const airportTaxes: Record<string, number> = {
          AGST: 0,
          YQ: 0,
          YR: 0,
          K3: 0,
          OC: 0,
          OT: 0,
        };

        fareDetails?.AirportTaxes?.forEach(({ Tax_Code, Tax_Amount }: any) => {
          if (knownTaxKeys.includes(Tax_Code)) {
            airportTaxes[Tax_Code] += Tax_Amount;
          } else {
            airportTaxes.OT += Tax_Amount;
          }
        });

        const seatAmount = traveller?.SSRDetails.filter(
          (item: any) => item.SSR_Type === 3
        ).reduce((sum: any, item: any) => sum + (item.Total_Amount || 0), 0);

        const mealAmount = traveller?.SSRDetails.filter(
          (item: any) => item?.SSR_Type === 1 || item?.SSR_Type === 2
        ).reduce((sum: any, item: any) => sum + (item.Total_Amount || 0), 0);

        const baggageAmount = traveller?.SSRDetails.filter(
          (item: any) => item.SSR_Type === 0
        ).reduce((sum: any, item: any) => sum + (item.Total_Amount || 0), 0);

        bookingData?.passenger_details?.push({
          initial: traveller?.Title,
          first_name: traveller?.First_Name,
          etrav_service_fee: fareDetails?.Service_Fee_Amount || 0,
          etrav_commision_fee: fareDetails?.GST || 0,
          etrav_gross_amount: fareDetails?.Gross_Commission || 0,
          etrav_net_amount: fareDetails?.Net_Commission || 0,
          last_name: traveller?.Last_Name,
          gender: traveller?.Gender === 0 ? "Male" : "Female",
          pax_type:
            traveller?.Pax_type === 0
              ? "Adult"
              : traveller?.Pax_type === 1
              ? "Child"
              : "Infant",
          date_of_birth: traveller?.DOB
            ? moment(traveller?.DOB, "MM/DD/YYYY").format(
                "YYYY-MM-DD[T]HH:mm:ss"
              )
            : null,
          passport_no: traveller?.Passport_Number,
          expiry_date: traveller?.Passport_Expiry
            ? moment(traveller?.Passport_Expiry, "MM/DD/YYYY").format(
                "YYYY-MM-DD[T]HH:mm:ss"
              )
            : null,
          issue_country_code: traveller?.Passport_Issuing_Country,
          issue_date: traveller?.Passport_Issue_Date
            ? moment(traveller?.Passport_Issue_Date, "MM/DD/YYYY").format(
                "YYYY-MM-DD[T]HH:mm:ss"
              )
            : null,
          ticket_number:
            traveller?.TicketDetails &&
            traveller?.TicketDetails[0] &&
            traveller?.TicketDetails[0]?.Ticket_Number, // TODO make it dynamic based on special roundtrip case
          journey_type:
            (reprintResponse?.Booking_Type === 1 ||
              reprintResponse?.Booking_Type === 2) &&
            pnrDetail?.Flights?.length > 1
              ? "Special Round Trip"
              : reprintResponse?.AirPNRDetails?.length > 1
              ? outerIndex === 0
                ? "Onward"
                : "Return"
              : "",
          check_in_baggage: baggage_details?.Check_In_Baggage,
          hand_baggage: baggage_details?.Hand_Baggage,
          display_remarks: baggage_details?.DisplayRemarks,
          SSR_details: traveller?.SSRDetails?.map((ssr: any) => {
            return {
              flight_id: ssr?.Flight_ID,
              SSR_code: ssr?.SSR_Code,
              SSR_status: ssr?.SSR_Status,
              SSR_type: ssr?.SSR_Type,
              SSR_typedesc: ssr?.SSR_TypeDesc,
              SSR_typename: ssr?.SSR_TypeName,
              segment_id: ssr?.Segment_Id,
              total_amount: ssr?.Total_Amount,
            };
          }),
          status: pnrDetail?.Ticket_Status_Desc,
          nationality: traveller?.Nationality,
          pancard_number: traveller?.Pancard_Number,
          student_id: traveller?.Student_Id,
          defence_service_id: traveller?.DefenceServiceId,
          defence_issue_date: traveller?.DefenceIssueDate
            ? moment(traveller?.DefenceIssueDate, "MM/DD/YYYY").format(
                "YYYY-MM-DD[T]HH:mm:ss"
              )
            : null,
          defence_expiry_date: traveller?.DefenceExpiryDate
            ? moment(traveller?.DefenceExpiryDate, "MM/DD/YYYY").format(
                "YYYY-MM-DD[T]HH:mm:ss"
              )
            : null,
          FrequentFlyer_details: traveller?.FrequentFlyerDetails || null,
          pnr: pnrDetail?.Airline_PNR || "",
          BASEFARE: fareDetails?.Basic_Amount,
          AirportTaxAmount: fareDetails?.AirportTax_Amount,
          SEAT: seatAmount,
          MEAL: mealAmount,
          BAGG: baggageAmount,
          BAGG_ADD: 0, // TODO : make it dynamic when key confirm
          ANCIL: 0,
          TOTAL: fareDetails?.Total_Amount, //TODO make it minus service fee amount
          // ServiceFeeAmount: fareDetails?.Service_Fee_Amount,
          ServiceFeeAmount:
            traveller?.Pax_type === 0 || traveller?.Pax_type === 1 // Adult or Child
              ? perEligiblePaxFee
              : 0, // Infant

          TDS: fareDetails?.TDS,
          ...airportTaxes,
        });
      });
    }
  );

  return bookingData;
};

export const formatTempBookingData = (tripJackResponse: any) => {
  const RefundableType =
    tripJackResponse?.repriceResponse &&
    tripJackResponse?.repriceResponse[0] &&
    tripJackResponse?.repriceResponse[0]?.Flight &&
    tripJackResponse?.repriceResponse[0]?.Flight[0]?.Fares &&
    tripJackResponse?.repriceResponse[0]?.Flight[0]?.Fares[0]?.Refundable
      ? "Refundable"
      : "Non Refundable";

  const user_guid = getLocalItem("user_guid");

  const faredetails = calculateFareDetails(
    tripJackResponse?.repriceResponse || [],
    tripJackResponse?.passengerCount
  );

  const bookingData: any = {
    user_guid: user_guid,
    booking_status: "Pending",
    convenience_fee: tripJackResponse?.convenienceFeeWithGST,
    convenience_fee_gst_per: tripJackResponse?.convenienceFeeGSTPER,
    PromoCodeID: tripJackResponse?.appliedPromo?.ID,
    PromoCode: tripJackResponse?.appliedPromo?.PromoCode,
    PromoCodeValue: tripJackResponse?.appliedPromoAmount,
    GiftCode: tripJackResponse?.appliedGiftCode,
    GiftCodeValue: tripJackResponse?.appliedGiftCodeAmount,
    wallet_balance: tripJackResponse?.walletAmount,
    refundable_walletamount: tripJackResponse?.walletRefundableAmount,
    non_refundable_walletamount: tripJackResponse?.walletNonRefundableAmount,
    IsCreditLimitUsed: tripJackResponse?.creditLimitAmount > 0,
    CreditLimitUsedAmount: tripJackResponse?.creditLimitAmount,
    booking_date: moment().format("YYYY-MM-DD[T]HH:mm:ss"),
    amount:
      (faredetails?.adults?.total || 0) +
      (faredetails?.children?.total || 0) +
      (faredetails?.infants?.total || 0) +
      // (faredetails?.overall?.serviceAmount || 0) +
      // (tripJackResponse?.convenienceFeeWithGST || 0) +
      (tripJackResponse?.totalSeatsAmount || 0) +
      (tripJackResponse?.totalBaggageAmount || 0) +
      (tripJackResponse?.totalWheelChairAmount || 0) +
      (tripJackResponse?.totalMealAmount || 0),
    email_id: tripJackResponse?.deliveryInfo?.emails[0],
    mobile_no: tripJackResponse?.deliveryInfo?.contacts[0],
    // alt_email_id: reprintResponse?.deliveryInfo?.emails[1] || "",
    // alt_mobile_no: reprintResponse?.deliveryInfo?.contacts[1] || "",
    gst_number: tripJackResponse?.gstInfo?.gstNumber,
    // gst_email: reprintResponse?.CustomerDetail?.GST_Number,
    gst_registred_name: tripJackResponse?.gstInfo?.registeredName,
    // gst_mobile_no: reprintResponse?.CustomerDetail?.GST_Number,
    gst_address: tripJackResponse?.gstInfo?.address,
    // state_id: tripJackResponse?.gstInfo?.state_id,
    base_fare:
      (faredetails?.adults?.basic || 0) +
      (faredetails?.children?.basic || 0) +
      (faredetails?.infants?.basic || 0),
    airline_gst:
      (faredetails?.adults?.gst || 0) +
      (faredetails?.children?.gst || 0) +
      (faredetails?.infants?.gst || 0),
    total_airline_tax:
      (faredetails?.adults?.taxes || 0) +
      (faredetails?.children?.taxes || 0) +
      (faredetails?.infants?.taxes || 0),
    service_fee: faredetails?.overall?.serviceAmount || 0,
    // (faredetails?.overall?.serviceAmount || 0),
    // // other_taxes: itemInfos?.AIR?.totalPriceInfo?.totalFareDetail?.afC?.TAF?.OT,
    // // yr: itemInfos?.AIR?.totalPriceInfo?.totalFareDetail?.afC?.TAF?.YR,
    // // oc: itemInfos?.AIR?.totalPriceInfo?.totalFareDetail?.afC?.TAF?.OC,
    net_price:
      (faredetails?.adults?.total || 0) +
      (faredetails?.children?.total || 0) +
      (faredetails?.infants?.total || 0) +
      // (faredetails?.overall?.serviceAmount || 0) +
      // (tripJackResponse?.convenienceFeeWithGST || 0) +
      (tripJackResponse?.totalSeatsAmount || 0) +
      (tripJackResponse?.totalWheelChairAmount || 0) +
      (tripJackResponse?.totalBaggageAmount || 0) +
      (tripJackResponse?.totalMealAmount || 0),
    fare_rules: RefundableType, // TODO make it segment wise instead of common
    flight_details: [],
    passenger_details: [],
  };

  tripJackResponse?.repriceResponse?.forEach((tripInfo: any) => {
    tripInfo?.Flight?.Segments?.forEach((segment: any) => {
      const fareClass =
        tripInfo?.Flight?.Fares &&
        tripInfo?.Flight?.Fares[0]?.FareDetails &&
        tripInfo?.Flight?.Fares[0]?.FareDetails[0] &&
        tripInfo?.Flight?.Fares[0]?.FareDetails[0]?.FareClasses &&
        tripInfo?.Flight?.Fares[0]?.FareDetails[0]?.FareClasses[0] &&
        tripInfo?.Flight?.Fares[0]?.FareDetails[0]?.FareClasses[0];

      bookingData?.flight_details?.push({
        airline_name: segment?.Airline_Name,
        airline_code: segment?.Airline_Code,
        flight_no: segment?.Flight_Number,
        flight_class: fareClass?.Class_Desc,
        flight_class_code: fareClass?.Class_Code,
        flight_type: tripJackResponse?.tripType || "ONEWAY",
        departure_date_time: moment(
          segment?.Departure_DateTime,
          "MM/DD/YYYY HH:mm:ss"
        ).format("YYYY-MM-DD[T]HH:mm:ss"),
        departure_place: segment?.Origin,
        departure_airport: segment?.Origin,
        departure_terminal: segment?.Origin_Terminal,
        arrival_date_time: moment(
          segment?.Arrival_DateTime,
          "MM/DD/YYYY HH:mm:ss"
        ).format("YYYY-MM-DD[T]HH:mm:ss"),
        arrival_place: segment?.Destination,
        arrival_airport: segment?.Destination,
        arrival_terminal: segment?.Destination_Terminal,
        duration: segment?.Duration,
        baggage_information: "",
        stop_details:
          !segment?.Stop_Over || segment?.Stop_Over === 0
            ? "Non-stop"
            : segment?.Stop_Over + "Stop",
      });
    });
  });

  tripJackResponse?.travellerInfos?.forEach((traveller: any) => {
    bookingData?.passenger_details?.push({
      initial: traveller?.Title,
      first_name: traveller?.First_Name,
      last_name: traveller?.Last_Name,
      gender:
        traveller?.Title === "MR" || traveller?.Title === "MSTR"
          ? "Male"
          : "Female",
      pax_type:
        traveller?.Pax_type === "0"
          ? "Adult"
          : traveller?.Pax_type === "1"
          ? "Child"
          : "Infant",
      date_of_birth: traveller?.DOB
        ? moment(traveller?.DOB, "MM/DD/YYYY").format("YYYY-MM-DD[T]HH:mm:ss")
        : null,
      passport_no: traveller?.Passport_Number,
      expiry_date: traveller?.Passport_Expiry
        ? moment(traveller?.Passport_Expiry, "MM/DD/YYYY").format(
            "YYYY-MM-DD[T]HH:mm:ss"
          )
        : null,
      issue_country_code: traveller?.Passport_Issuing_Country,
      // need to add one more field here for the passport issue date and also if possible then change issue country to country code
      status: "Pending",
      nationality: traveller?.Nationality,
      pancard_number: traveller?.Pancard_Number,
      student_id: traveller?.Student_Id,
      defence_service_id: traveller?.DefenceServiceId,
      defence_issue_date: traveller?.DefenceIssueDate
        ? moment(traveller?.DefenceIssueDate, "MM/DD/YYYY").format(
            "YYYY-MM-DD[T]HH:mm:ss"
          )
        : null,
      defence_expiry_date: traveller?.DefenceExpiryDate
        ? moment(traveller?.DefenceExpiryDate, "MM/DD/YYYY").format(
            "YYYY-MM-DD[T]HH:mm:ss"
          )
        : null,
      FrequentFlyer_details: traveller?.FrequentFlyerDetails || null,
      // issue_date: null,
    });
  });

  return bookingData;
};
